import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ArrowRight } from 'lucide-react';
import type { HelpEntry, HelpLink } from '../../data/helpTextTypes';
import { ADMIN_NAVIGATE_EVENT } from '../../data/helpTextTypes';

// ──────────────────────────────────────────────────────────────────────────
// HelpPanel — slide-out drawer for contextual help (domain-agnostic).
// ──────────────────────────────────────────────────────────────────────────
//
// Why a slide-out rather than a modal:
//   The task spec explicitly calls for a non-blocking panel so reviewers
//   can still see and interact with the underlying screen while reading
//   the help text. A modal would force-close on any click outside, which
//   kills that use case.
//
// The panel is positioned fixed on the right edge, slides in via a
// translate-x transform, and does NOT render a full-screen backdrop.
// A small dimming gradient is applied behind the panel for visual focus,
// but pointer events on that region pass through to the page underneath
// (pointer-events-none on the gradient div).
//
// Domain-agnostic: this component takes a HelpEntry directly (not a
// screenId). The caller (typically a domain-specific HelpIconButton
// wrapper) is responsible for looking up the entry from its own data
// source. This keeps HelpPanel reusable across Trust Portal, Admin
// Security, and any future help-enabled surface.
//
// Internal navigation: if the entry has `relatedLinks`, they render at
// the bottom. Clicking a link dispatches a `jarvis-admin-navigate`
// custom event on window with the target payload — Admin.tsx listens
// for this event and updates its internal state to jump the user to
// the requested screen. This indirection avoids prop-drilling
// navigation state through every screen that renders a help icon.

interface HelpPanelProps {
  entry: HelpEntry;
  isOpen: boolean;
  onClose: () => void;
}

const HelpPanel: React.FC<HelpPanelProps> = ({ entry, isOpen, onClose }) => {
  // Close on Escape for keyboard accessibility. Only attached while open
  // so we don't leak a global listener onto every page.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleRelatedLinkClick = (link: HelpLink) => {
    // Dispatch a global custom event; Admin.tsx (or any listener) picks
    // up the target tab payload and updates its state. HelpPanel itself
    // knows nothing about Admin.tsx's state shape — this keeps the panel
    // reusable across any host component.
    const event = new CustomEvent(ADMIN_NAVIGATE_EVENT, {
      detail: {
        targetTopTab: link.targetTopTab,
        targetSubTab: link.targetSubTab,
        targetSubSubTab: link.targetSubSubTab,
      },
    });
    window.dispatchEvent(event);
    // Auto-close the panel so the user can see the destination screen
    // immediately. If a future use case needs the panel to stay open,
    // we can make this behavior opt-in via a prop.
    onClose();
  };

  const hasRelatedLinks = Array.isArray(entry.relatedLinks) && entry.relatedLinks.length > 0;

  const panel = (
    <>
      {/* Soft focus gradient — visual only, pointer events disabled so the
          underlying page stays fully interactive per the task spec. */}
      <div
        className={`fixed inset-0 pointer-events-none transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background:
            'linear-gradient(to left, rgba(15, 23, 42, 0.12) 0%, rgba(15, 23, 42, 0) 40%)',
          zIndex: 998,
        }}
        aria-hidden="true"
      />

      {/* The drawer itself. Uses transform so the GPU handles the slide
          and there is no layout thrash on the underlying page. */}
      <aside
        role="dialog"
        aria-modal="false"
        aria-labelledby="jarvis-help-title"
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl border-l border-gray-200 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ zIndex: 999 }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
              Help &amp; Context
            </p>
            <h2
              id="jarvis-help-title"
              className="mt-1 text-lg font-bold text-gray-900 leading-snug"
            >
              {entry.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close help panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body — four-part structure matches the handoff doc. */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5 text-sm text-gray-700 leading-relaxed">
          {entry.whatItDoes && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                What this screen does
              </h3>
              <p>{entry.whatItDoes}</p>
            </section>
          )}

          {entry.howToNavigate && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                How to navigate it
              </h3>
              <p>{entry.howToNavigate}</p>
            </section>
          )}

          {entry.whatItContains && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                What information it contains
              </h3>
              <p>{entry.whatItContains}</p>
            </section>
          )}

          {entry.keyActions && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                Key actions available
              </h3>
              <p>{entry.keyActions}</p>
            </section>
          )}

          {hasRelatedLinks && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Related — jump to another screen
              </h3>
              <div className="space-y-2">
                {entry.relatedLinks!.map((link, idx) => (
                  <button
                    key={`${link.label}-${idx}`}
                    type="button"
                    onClick={() => handleRelatedLinkClick(link)}
                    className="w-full text-left p-3 rounded-lg border border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-blue-900">{link.label}</div>
                        {link.description && (
                          <div className="mt-0.5 text-xs text-blue-800/80 leading-snug">
                            {link.description}
                          </div>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-blue-700 mt-0.5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
          Press <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">Esc</kbd> or click the X to close. The underlying screen remains interactive while this panel is open.
        </div>
      </aside>
    </>
  );

  // Rendered via portal so the drawer isn't clipped by parent overflow
  // containers (the Trust Portal layout uses overflow-hidden for the
  // scrollable content area).
  return ReactDOM.createPortal(panel, document.body);
};

export default HelpPanel;
